import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useLocation, Link as RouterLink, useNavigate } from 'react-router-dom'
import { Box, Button, Drawer, Grid, Theme } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import { useSelector, useDispatch } from 'react-redux'
import { getPendingActionItems } from 'apis/actionNeeded'
import { setConsolidatorActionItemsCount } from 'slices/actionNeeded'
import { getReportingSectionProps } from 'services/dashboardSidebar.service'
import { isEmpty } from 'lodash'
import { selectAppointmentRequestsCount } from '../../selectors/apptRequests'
import { DRAWER_WIDTH, FOOTER_HEIGHT, HEADER_HEIGHT, RequestLevel, ROUTES } from '../../constants'
import Scrollbar from '../Scrollbar'
import { useCurrentPractice } from '../../contexts/PracticeContext'
import {
	isCheckOnlyPractice,
	isFinancingOnlyPractice,
	isIndependentPractice,
	isRebateEnabled,
	shouldShowCommunicationsSection,
	shouldShowIndustryProgramsPage
} from '../../util/practice'
import { shouldDisplayIndustryProgramsForConsolidator } from '../../util/consolidator'
import { useConsolidatorFeatureFlag } from '../../hooks/useConsolidatorFeatureFlag'
import { selectContext, selectUser } from '../../selectors/auth'
import { isAdminUser, isScratchAdminUser } from '../../util/user'
import CustomizedLabels from '../../util/CustomizedLabels'
import { getAdminSectionProps } from '../../util/consolidator'
import { RootState } from '../../types/store'
import { isManagerOrAdmin } from '../../util/unified'
import AccordionSection from '../AccordionSection'
import { CustomBadge } from '../common/CustomBadge'
import { ScratchLogo } from 'components/common/ScratchLogo'
import SidebarAds from './sidebarAds/SidebarAds'
import { useReadOnlyUser } from './guards/ReadOnlyUserGuard/useReadOnlyUser'
import { useStaffPaymentsOnly } from '../../hooks/useStaffPaymentsOnly'
import ReferralProgramButton from './ReferralProgramButton'
import {
	CHECKOUT_FAQ_URL,
	CHECK_ONLY_FAQ_URL,
	LENDING_FAQ_URL,
	ResourcesMenu,
	SIDEBAR_PILL_SX
} from './resourcesMenu'
import CapitalPromoTile from './CapitalPromoTile'
import CapitalGuideBanner from './CapitalGuideBanner'
import { useCapitalAccess } from '../../hooks/useCapitalAccess'
import { useCapitalActivePayoutsAccess } from '../../hooks/useCapitalActivePayoutsAccess'
import { cardRequestsEnabled } from './practices/helpers'

export interface NavItem {
	title: string
	path: string
	icon?: React.ReactNode
	practiceAllowedProperty?: string
	chip?: React.ReactNode
}

const actionNeededLink = (actionNeededCount?: number): NavItem => ({
	title: 'Action Items',
	path: '/dashboard/ActionNeeded',
	icon: <CustomBadge color="error" badgeContent={actionNeededCount} />
})

const getPaymentSection = (
	disputesNotificationCount: number | undefined,
	isSMBPractice: boolean,
	isCurrentPracticeActive: boolean = true,
	isUserReadOnly: boolean,
	showCardRequests: boolean = false
): { title: string; items: NavItem[] }[] => [
	{
		title: 'Payments',
		items: [
			...((isSMBPractice && !isCurrentPracticeActive) || isUserReadOnly
				? []
				: [{ title: 'Collect Payment', path: '/dashboard/collect-payment' }]),
			...(isUserReadOnly ? [] : [{ title: 'Open Invoices', path: '/dashboard/invoices' }]),
			...(showCardRequests
				? [{ title: 'Card on File Requests', path: '/dashboard/card-requests' }]
				: []),
			{
				title: 'Disputes',
				path: '/dashboard/disputes',
				icon: <CustomBadge color="error" badgeContent={disputesNotificationCount} />
			}
		]
	}
]

const getReportingSection = (
	reportingSectionProps: ReportingSectionProps,
	actionNeededCount?: number,
	capitalActivePayoutsCanAccess = false
): { title: string; items: NavItem[] }[] => {
	const sections: { title: string; items: NavItem[] }[] = [
		{
			title: 'Reporting',
			items: [
				{
					title: 'Daily Totals',
					path: '/dashboard/dailytotals'
				},
				{
					title: 'Payment History',
					path: '/dashboard/payments'
				},
				{
					...actionNeededLink(actionNeededCount)
				}
			]
		}
	]

	if (reportingSectionProps.showMonthlyStatements) {
		// enabled for independents
		sections[0].items.push({
			title: 'Monthly Statements',
			path: '/dashboard/monthlyTotals'
		})
	}

	if (reportingSectionProps.showDailyCashCountPage) {
		sections[0].items.push(
			{
				title: 'Cash Drawer Totals',
				path: '/dashboard/cash-drawer'
			},
			{
				title: 'Cash Counts',
				path: '/dashboard/cash-counts'
			}
		)
	}

	if (reportingSectionProps.showCheckPayments || reportingSectionProps.showECheckReportingPage) {
		sections[0].items.push({
			title: 'eCheck Reporting',
			path: '/dashboard/checkReporting'
		})
	}
	if (!reportingSectionProps.hideReconciliation) {
		sections[0].items.push({
			title: 'Reconciliation',
			path: '/dashboard/reconciliation'
		})
	}

	if (!reportingSectionProps.hidePayoutsPage) {
		sections[0].items.push({
			title: 'Payouts',
			path: capitalActivePayoutsCanAccess ? '/dashboard/stripe-payouts' : '/dashboard/payouts-v2'
		})
	}

	if (reportingSectionProps.showInstallmentPlans) {
		sections[0].items.push({
			title: CustomizedLabels.getCustomLabel(
				'installmentPlans.reporting.sidebarTitle',
				'Recurring Payments'
			),
			path: '/dashboard/installment-plans'
		})

		sections[0].items.push({
			title: CustomizedLabels.getCustomLabel(
				'installmentPlans.reporting.pastDue.sidebarTitle',
				'Late Recurring Payments'
			),
			path: '/dashboard/past-due-plans'
		})
	}

	if (reportingSectionProps.showSavedCards) {
		sections[0].items.push({
			title: 'Stored Cards',
			path: '/dashboard/storedcard'
		})
	}

	if (reportingSectionProps.showRefundRequests) {
		sections[0].items.push({
			title: 'Refund Requests',
			path: '/dashboard/refund-requests'
		})
	}

	if (reportingSectionProps.showTipReport) {
		sections[0].items.push({
			title: 'Tip Report',
			path: '/dashboard/tip-report'
		})
	}

	return sections
}

const independentPaymentPracticesSection: { title: string; items: NavItem[] }[] = [
	{
		title: 'Settings',
		items: [
			{
				title: 'Banking Information',
				path: '/settings/banking-info'
			},
			{
				title: 'Required Information',
				path: '/settings/required-info'
			}
		]
	}
]

export const getAdminSectionItems = (
	props: AdminSectionProps
): { title: string; items: NavItem[] }[] => {
	const items: { title: string; items: NavItem[] }[] = [
		{
			title: 'Administrator',
			items: [
				{
					title: 'Locations',
					path: props.useAdminLocationsV2 ? '/dashboard/practices-v2' : '/dashboard/practices'
				},
				{
					title: 'Users',
					path: '/dashboard/users'
				},
				{
					title: 'All Open Invoices',
					path: '/dashboard/all-invoices'
				},
				{
					title: 'All Payouts',
					path: '/dashboard/allpayouts-v2'
				}
			]
		}
	]

	if (props.showConsolidatorPaymentHistory) {
		items[0].items.splice(2, 0, {
			title: 'All Payment History',
			path: '/dashboard/all-payments'
		})
	}

	if (props.showAllRecurringPayments) {
		items[0].items.splice(3, 0, {
			title: 'All Recurring Payments',
			path: '/dashboard/all-recurring-payments'
		})
	}

	if (props.showDailyCashCountPage) {
		items[0].items.splice(3, 0, {
			title: 'All Cash Counts',
			path: '/dashboard/all-cash-counts'
		})
	}

	if (props.showVendorNamesPage) {
		items[0].items.splice(7, 0, {
			title: 'Vendor Names',
			path: '/VendorNames'
		})
	}

	if (props.showOpenActionItemsPage) {
		items[0].items.splice(8, 0, {
			title: 'Open Action Items',
			path: '/dashboard/open-action-items',
			icon: <CustomBadge badgeContent={props.openActionItemsCount} />
		})
	}

	if (props.showHelpDeskPage) {
		items[0].items.splice(9, 0, {
			title: 'Help Desk',
			path: '/dashboard/help-desk'
		})
	}

	if (props.showPostcardPage) {
		items[0].items.splice(10, 0, {
			title: 'Postcard',
			path: '/dashboard/postcard-file'
		})
	}

	if (props.showLockboxChecksPage) {
		items[0].items.splice(11, 0, {
			title: 'Lockbox Checks',
			path: '/LockboxChecks'
		})
	}

	if (props.showCheckReportingPage) {
		items[0].items.splice(6, 0, {
			title: 'eCheck and Lockbox Report',
			path: '/dashboard/echeck-lockbox-report'
		})
	}

	if (props.showAllDisputesPage) {
		items[0].items.push({
			title: 'All Disputes',
			path: '/dashboard/all-disputes'
		})
	}

	if (props.showDailyTransactionFeed) {
		items[0].items.push({
			title: 'Daily Transaction Feed',
			path: '/dashboard/daily-feed'
		})
	}

	if (props.showConsolidatorMonthlyReport) {
		items[0].items.push({
			title: 'Monthly Reports',
			path: '/dashboard/monthly-report'
		})
	}

	if (props.showEnterpriseRebateHistory) {
		items[0].items.push({
			title: 'All Rebate History',
			path: '/dashboard/all-rebate-history'
		})
	}

	if (props.showRebatePayoutsPage) {
		items[0].items.push({
			title: 'Rebate Payouts',
			path: '/dashboard/rebate-payouts'
		})
	}

	// Added last so the fixed splice indexes above stay valid; placed right after All Open Invoices
	if (props.showAllCardRequestsPage) {
		const allInvoicesIndex = items[0].items.findIndex(
			(item) => item.path === '/dashboard/all-invoices-v2'
		)
		items[0].items.splice(allInvoicesIndex + 1, 0, {
			title: 'Card on File Requests',
			path: '/dashboard/all-card-requests'
		})
	}

	return items
}

const sectionScratchAdmin: { title: string; items: NavItem[] }[] = [
	{
		title: 'Scratch Management',
		items: [
			{
				title: 'All Disputes',
				path: '/dashboard/alldisputes'
			},
			{
				title: 'All Locations',
				path: '/dashboard/allpractices'
			},
			{
				title: 'All Consolidated Practices',
				path: '/dashboard/all-consolidated-practices'
			},
			{
				title: 'Business Verification',
				path: '/dashboard/business-verification'
			},
			{
				title: 'PIMS Data Sync',
				path: '/dashboard/pims-data-sync'
			},
			{
				title: 'Proxy Routing',
				path: '/dashboard/proxy-routing'
			},
			{
				title: 'Feature Flags',
				path: '/dashboard/feature-flags'
			},
			{
				title: 'All Rebates',
				path: '/dashboard/all-rebates'
			},
			{
				title: 'Rebate Product Catalog',
				path: '/dashboard/rebates-product-catalog'
			},
			{
				title: 'Terminal Orders',
				path: '/dashboard/terminal-orders'
			},
			{
				title: 'Readers Reporting',
				path: '/dashboard/readers-reporting'
			},
			{
				title: 'Accounts Invitations',
				path: '/dashboard/accounts/invitations'
			},
			{
				title: 'Opt Outs',
				path: '/dashboard/opt-outs'
			},
			{
				title: 'Custom Payouts',
				path: '/dashboard/custom-payouts'
			},
			{
				title: 'Manual Payout Batches',
				path: '/dashboard/custom-payouts/manual-batches'
			},
			{
				title: 'Practice Fee Settings',
				path: '/dashboard/practice-fee-settings'
			},
			{
				title: 'Rate Change History',
				path: '/dashboard/practice-fee-logs'
			},
			{
				title: 'Billing Admin',
				path: '/dashboard/billing-admin'
			},
			{
				title: 'Terminal Rebate Setup',
				path: '/dashboard/practice-terminal-rebate-setup'
			},
			{
				title: 'Rebate Programs',
				path: '/dashboard/rebate-programs'
			},
			{
				title: 'B2B Rebate Files',
				path: '/dashboard/b2b-rebate-files'
			},
			{
				title: 'Refund Admin',
				path: '/dashboard/refund-admin'
			},
			{
				title: 'Charity Accounts',
				path: '/dashboard/charity-accounts'
			}
		]
	}
]

const sectionCommunications = (
	appointmentRequestCount,
	isDirectBookingEnabled,
	unreadMessages
): { title: string; items: NavItem[] }[] => [
	{
		title: 'Communications',
		items: [
			{
				title: 'Chat',
				path: '/dashboard/chat/new',
				icon: <CustomBadge color="error" badgeContent={unreadMessages} />,
				practiceAllowedProperty: 'isChatEnabled'
			},
			{
				title: 'Reminders',
				path: '/communication/reminders/reporting',
				practiceAllowedProperty: 'isRemindersEnabled'
			},
			{
				title: isDirectBookingEnabled ? 'Appointments' : 'Appt. Requests',
				path: '/communication/appointment-requests',
				icon: <CustomBadge color="error" badgeContent={appointmentRequestCount} />,
				practiceAllowedProperty: 'isAppointmentRequestsEnabled'
			},
			{
				title: 'Forms',
				path: '/communication/forms',
				practiceAllowedProperty: 'isFormsEnabled'
			},
			{
				title: 'Reputation',
				path: '/communication/reputation-management',
				practiceAllowedProperty: 'isReputationEnabled'
			},
			{
				title: 'Opt-outs',
				path: '/communication/opt-outs',
				practiceAllowedProperty: 'isOptOutsEnabled'
			},
			{
				title: 'Priority Call List',
				path: '/communication/priority-call-list',
				practiceAllowedProperty: 'isPriorityCallListEnabled'
			},
			{
				title: 'One-time',
				path: '/communication/one-time-email/create',
				practiceAllowedProperty: 'isOneTimeEmailsEnabled'
			}
		]
	}
]

const sectionIndustryPrograms: { title: string; items: NavItem[] }[] = [
	{
		title: 'Industry Programs',
		items: [
			{
				title: 'Active Programs',
				path: '/programs/partnerships'
			}
		]
	}
]

const getRebatesSection = (props: RebatesSectionProps): { title: string; items: NavItem[] }[] => {
	const items: { title: string; items: NavItem[] }[] = [
		{
			title: 'Rebate Programs',
			items: [
				{
					title: 'Enrollment',
					path: '/rebates/enrollment'
				},
				{
					title: 'Rebates Reporting',
					path: '/rebates/reporting'
				}
			]
		}
	]

	if (props.showRebateBankInformation) {
		items[0].items.push({
			title: 'Banking Information',
			path: '/rebates/banking-info'
		})
	}

	return items
}

const sectionCapital: { title: string; items: NavItem[] }[] = [
	{
		title: 'Capital',
		items: [
			{
				title: 'Financing',
				path: '/financing/capital'
			}
		]
	}
]

const sectionFinancing: { title: string; items: NavItem[] }[] = [
	{
		title: 'Financing',
		items: [
			{
				title: 'Plans',
				path: '/financing/plans'
			},
			{
				title: 'Get Started',
				path: '/financing/get-started'
			},
			{
				title: 'Training',
				path: '/financing/training'
			},
			{
				title: 'Account',
				path: `/${ROUTES.accountUri}`
			}
		]
	}
]

const useStyles = makeStyles()(() => ({
	logo: {
		height: 28
		// marginRight: theme.spacing(2)
	},
	drawerList: {
		width: DRAWER_WIDTH
	},
	spacer: {
		flexGrow: 1
	},
	sidebarContainer: {
		'& .MuiBox-root .MuiListItem-root a, & .MuiButton-text': {
			padding: '3px 6px'
		},
		'& .MuiBox-root .MuiListItem-root a:not(.active), & .MuiButton-text:not(.active)': {
			color: 'inherit'
		},
		'& .MuiSvgIcon-fontSizeSmall': {
			padding: '2px'
		}
	}
}))

interface DashboardSidebarProps {
	onMobileClose?: (...args: any[]) => any
	openMobile?: boolean
}

const DashboardSidebar = (props: DashboardSidebarProps) => {
	const { onMobileClose, openMobile } = props
	const {
		userRolePractice,
		currentPracticeObject,
		isPaymentEnabled,
		disputesNotificationCount,
		currentUserConsolidator,
		isCurrentPracticeActive,
		canAccessFinancingDev: showFinancingDev
	} = useCurrentPractice()
	const location = useLocation()
	const navigate = useNavigate()
	const user = useSelector(selectUser)
	const context = useSelector(selectContext)
	const appointmentRequestCount = useSelector(selectAppointmentRequestsCount)
	const { unreadMessages } = useSelector((state: any) => state?.chat)
	const { actionNeededCount, consolidatorActionItemsCount } = useSelector(
		(state: RootState) => state.actionNeeded
	)
	const dispatch = useDispatch()
	const { isReadOnly: isUserReadOnly } = useReadOnlyUser()
	const { restrictStaffToPaymentsOnly } = useStaffPaymentsOnly()

	const admin = user?.isAdmin || false
	const scratchAdmin = isScratchAdminUser(user)
	const { classes } = useStyles()

	const [sumUnreadMessages, setSumUnreadMessages] = useState(0)

	const isSMBPractice = useMemo(
		() => !isEmpty(currentPracticeObject) && isIndependentPractice(currentPracticeObject),
		[currentPracticeObject]
	)

	const isLendingOnlyPractice = useMemo(
		() => isFinancingOnlyPractice(currentPracticeObject, isPaymentEnabled),
		[currentPracticeObject]
	)

	const showCommunicationsSection = useMemo(
		() => shouldShowCommunicationsSection(currentPracticeObject),
		[currentPracticeObject]
	)

	const showIndustryProgramsPage = useMemo(() => {
		if (isSMBPractice) {
			return shouldShowIndustryProgramsPage(currentPracticeObject)
		} else if (isAdminUser(user) || isScratchAdminUser(user)) {
			if (
				currentUserConsolidator?.id &&
				shouldDisplayIndustryProgramsForConsolidator(currentUserConsolidator)
			) {
				return true
			}
			return shouldShowIndustryProgramsPage(currentPracticeObject)
		}
		return false
	}, [currentPracticeObject, currentUserConsolidator, isSMBPractice])

	const showRebatesProgramsPage = useMemo(() => {
		return isRebateEnabled(currentPracticeObject)
	}, [currentPracticeObject])

	const reportingSectionProps: ReportingSectionProps = useMemo(
		() => getReportingSectionProps(currentPracticeObject, currentUserConsolidator),
		[currentPracticeObject, currentUserConsolidator]
	)

	const { canAccess: capitalActivePayoutsCanAccess } = useCapitalActivePayoutsAccess()

	const paymentSection = useMemo(
		() =>
			getPaymentSection(
				disputesNotificationCount,
				isSMBPractice,
				isCurrentPracticeActive,
				isUserReadOnly,
				cardRequestsEnabled(currentPracticeObject, currentUserConsolidator)
			),
		[
			disputesNotificationCount,
			isCurrentPracticeActive,
			isUserReadOnly,
			currentPracticeObject,
			currentUserConsolidator
		]
	)

	const reportingSection = useMemo(
		() =>
			getReportingSection(reportingSectionProps, actionNeededCount, capitalActivePayoutsCanAccess),
		[reportingSectionProps, actionNeededCount, capitalActivePayoutsCanAccess]
	)

	const isAdminContext = isAdminUser(user)
	const { enabled: showEnterpriseRebateHistory } = useConsolidatorFeatureFlag(
		'enableEnterpriseRebateHistory',
		{ pause: !isAdminContext }
	)
	const { enabled: showRebatePayoutsPage } = useConsolidatorFeatureFlag('enableRebatePayoutsPage', {
		pause: !isAdminContext
	})

	const sectionAdminProps: AdminSectionProps = useMemo(
		() => ({
			...getAdminSectionProps(currentUserConsolidator, consolidatorActionItemsCount),
			showEnterpriseRebateHistory,
			showRebatePayoutsPage,
			// Consolidator-wide report, so only the consolidator setting applies
			showAllCardRequestsPage: cardRequestsEnabled(undefined, currentUserConsolidator)
		}),
		[
			currentUserConsolidator,
			consolidatorActionItemsCount,
			showEnterpriseRebateHistory,
			showRebatePayoutsPage
		]
	)

	const sectionsAdmin = useMemo(() => getAdminSectionItems(sectionAdminProps), [sectionAdminProps])

	const communicationsSection = useMemo(
		() =>
			sectionCommunications(
				appointmentRequestCount,
				currentPracticeObject?.settings?.isDirectBookingEnabled,
				sumUnreadMessages
			),
		[
			appointmentRequestCount,
			currentPracticeObject?.settings?.isDirectBookingEnabled,
			sumUnreadMessages
		]
	)

	const isIndependentPracticeUser =
		!isEmpty(currentPracticeObject) &&
		isIndependentPractice(currentPracticeObject) &&
		isManagerOrAdmin(context, currentPracticeObject)

	const rebatesSection = useMemo(
		() =>
			getRebatesSection({
				showRebateBankInformation: isIndependentPracticeUser
			}),
		[currentPracticeObject, isIndependentPracticeUser]
	)

	const { canAccess: showStripeCapital } = useCapitalAccess()

	// TODO: Have an explicit "isPayments"/"isLending" flag on the consolidator.
	const showPayments = useMemo(
		() =>
			isPaymentEnabled && (currentPracticeObject?.stripeAccountId || currentUserConsolidator?.mcc),
		[currentPracticeObject, currentUserConsolidator, isPaymentEnabled]
	)

	const shouldFetchActionItemsForAdmin = useMemo(() => {
		return Boolean(
			user &&
			isAdminUser(user) &&
			currentUserConsolidator?.id &&
			sectionAdminProps?.showOpenActionItemsPage
		)
	}, [user, currentUserConsolidator?.id, sectionAdminProps?.showOpenActionItemsPage])

	const fetchConsolidatorIncompleteActionItems = useCallback(async () => {
		try {
			const res = await getPendingActionItems(RequestLevel.ADMIN)
			const totalCount = res.data?.listActionNeeded.total ?? 0
			dispatch(setConsolidatorActionItemsCount(totalCount))
		} catch (err) {
			console.error(`Failed to fetch consolidator incomplete action items: ${err}`)
		}
	}, [dispatch])

	useEffect(() => {
		const unreadCounts = Object.values(unreadMessages) as number[]
		const sumUnreadCount = unreadCounts.reduce((a: number, v: number) => (a += v), 0)
		setSumUnreadMessages(sumUnreadCount)
	}, [unreadMessages])

	useEffect(() => {
		if (!shouldFetchActionItemsForAdmin) {
			return
		}
		fetchConsolidatorIncompleteActionItems() // run immediately and then every 3 minutes

		const intervalId = setInterval(() => {
			fetchConsolidatorIncompleteActionItems()
		}, 180000)

		return () => clearInterval(intervalId)
	}, [fetchConsolidatorIncompleteActionItems, shouldFetchActionItemsForAdmin])

	const checkOnlySection: { title: string; items: NavItem[] }[] = [
		...(isSMBPractice && !isCurrentPracticeActive
			? []
			: [
					{
						title: 'Payments',
						items: [
							{
								title: 'Collect Payment',
								path: '/dashboard/collect-payment'
							}
						]
					}
				]),
		...(restrictStaffToPaymentsOnly
			? []
			: [
					{
						title: 'Reporting',
						items: [
							{
								title: 'Payment History',
								path: '/dashboard/payments'
							},
							{
								title: 'eCheck Reporting',
								path: '/dashboard/checkReporting'
							},
							{
								...actionNeededLink(actionNeededCount)
							}
						]
					}
				])
	]

	const showPaymentsSignUp = useMemo(
		() =>
			user && !showPayments && currentPracticeObject?.id && !currentPracticeObject?.consolidatorId,
		[user, showPayments, currentPracticeObject]
	)

	useEffect(() => {
		if (openMobile && onMobileClose) {
			onMobileClose()
		}
	}, [location.pathname])

	// Each collapsible section with its title and boolean conditional for being shown
	const sidebarSections = useMemo<
		{
			title: string
			condition: unknown
			items: NavItem[]
			signupLink?: string
			onAccordionTitleClick?: () => void
		}[]
	>(() => {
		if (restrictStaffToPaymentsOnly) {
			return [
				{
					title: 'Payments',
					condition: user && isPaymentEnabled,
					items: [{ title: 'Collect Payment', path: '/dashboard/collect-payment' }]
				}
			]
		}

		return [
			{
				title: paymentSection[0].title,
				condition: user && isPaymentEnabled,
				items: paymentSection[0].items
			},
			{
				title: reportingSection[0].title,
				condition: user && isPaymentEnabled,
				items: reportingSection[0].items
			},
			{
				title: sectionFinancing[0].title,
				condition: user && showFinancingDev,
				items: sectionFinancing[0].items
			},
			{
				title: sectionCapital[0].title,
				condition: user && showStripeCapital,
				items: sectionCapital[0].items
			},
			{
				title: 'Payments',
				condition: showPaymentsSignUp,
				signupLink: '/dashboard/payments/sign-up',
				items: []
			},
			{
				title: communicationsSection[0].title,
				condition: user && showCommunicationsSection,
				items: communicationsSection[0].items
			},
			{
				title: independentPaymentPracticesSection[0].title,
				condition: user && isIndependentPracticeUser && showPayments && isCurrentPracticeActive,
				items: independentPaymentPracticesSection[0].items
			},
			{
				title: sectionsAdmin[0].title,
				condition: user && admin === true,
				items: sectionsAdmin[0].items
			},
			{
				title: sectionScratchAdmin[0].title,
				condition: user && scratchAdmin === true,
				items: sectionScratchAdmin[0].items
			},
			{
				title: sectionIndustryPrograms[0].title,
				condition: user && showIndustryProgramsPage,
				items: sectionIndustryPrograms[0].items,
				onAccordionTitleClick: () => {
					sectionIndustryPrograms[0]?.items[0]?.path &&
						navigate(sectionIndustryPrograms[0].items[0].path)
				}
			},
			{
				title: rebatesSection[0].title,
				condition: user && showRebatesProgramsPage,
				items: rebatesSection[0].items
			}
		]
	}, [
		restrictStaffToPaymentsOnly,
		paymentSection,
		user,
		isPaymentEnabled,
		reportingSection,
		showFinancingDev,
		showStripeCapital,
		showPaymentsSignUp,
		communicationsSection,
		showCommunicationsSection,
		isIndependentPracticeUser,
		admin,
		userRolePractice?.hospitalManager,
		sectionsAdmin,
		scratchAdmin,
		isCurrentPracticeActive
	])

	const [expandedSection, setExpandedSection] = useState<string | null>(null)
	const [isInitialLoad, setIsInitialLoad] = useState(true)
	const userInteractionRef = useRef(false)

	const visibleSections = useMemo(
		() => sidebarSections.filter((section) => Boolean(section.condition)),
		[sidebarSections]
	)

	const currentSectionFromPath = useMemo(
		() =>
			visibleSections.find(
				(section) =>
					section.items?.some((item) => item.path === location.pathname) ||
					section.signupLink === location.pathname
			),
		[visibleSections, location.pathname]
	)

	useEffect(() => {
		if (!user || isEmpty(currentPracticeObject)) return

		if (isInitialLoad || (!expandedSection && visibleSections.length > 0)) {
			const firstVisibleSection = visibleSections[0]?.title
			if (firstVisibleSection) {
				setExpandedSection(firstVisibleSection)
			}
			setIsInitialLoad(false)
		} else if (
			currentSectionFromPath &&
			currentSectionFromPath.title !== expandedSection &&
			!userInteractionRef.current
		) {
			setExpandedSection(currentSectionFromPath.title)
		}

		userInteractionRef.current = false
	}, [
		user,
		currentPracticeObject,
		visibleSections,
		expandedSection,
		isInitialLoad,
		currentSectionFromPath,
		location.pathname
	])

	const handleAccordionChange = useCallback(
		(sectionTitle: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
			userInteractionRef.current = true
			setExpandedSection(isExpanded ? sectionTitle : null)
		},
		[]
	)

	const content = (
		<Box
			className={classes.sidebarContainer}
			sx={{
				display: 'flex',
				flexDirection: 'column',
				height: '100%'
			}}
		>
			<Scrollbar options={{ suppressScrollX: true }} style={{ flexGrow: 1 }}>
				<Box sx={{ flexGrow: 1, p: 2 }}>
					<Box sx={{ ml: 2 }} />
					<Box
						sx={{
							alignItems: 'center',
							borderRadius: 1,
							display: 'flex',
							overflow: 'hidden',
							p: 2
						}}
					/>
					<Box
						sx={{
							alignItems: 'left',
							display: 'flex',
							justifyContent: 'start',
							pl: 2
						}}
					>
						<RouterLink to="/">
							<ScratchLogo size={30} />
						</RouterLink>
					</Box>
					{visibleSections.map((section) => (
						<Box
							key={section.title}
							sx={(theme: Theme) => ({
								position: 'relative',
								'&::after': {
									content: '""',
									position: 'absolute',
									bottom: 0,
									left: 0,
									right: 0,
									height: '1px',
									backgroundColor: theme.palette.mode === 'dark' ? '#333545' : '#E8E8FF'
								},
								'&:last-child::after': {
									display: 'none'
								}
							})}
						>
							<AccordionSection
								key={section.title}
								title={section.title}
								items={section.items}
								pathname={location.pathname}
								expanded={expandedSection === section.title}
								onChange={handleAccordionChange(section.title)}
								signupLink={section.signupLink}
								badgeCount={getTotalBadgeCount(section.items)}
								onAccordionTitleClick={section.onAccordionTitleClick}
							/>
						</Box>
					))}
				</Box>
				{isLendingOnlyPractice && isSMBPractice && (
					<Box sx={{ p: 2, m: 2 }}>
						<SidebarAds />
					</Box>
				)}
			</Scrollbar>
			<BottomSection
				faqUrl={isLendingOnlyPractice ? LENDING_FAQ_URL : CHECKOUT_FAQ_URL}
				showResources={!isLendingOnlyPractice}
				hideSettings={restrictStaffToPaymentsOnly}
			/>
		</Box>
	)

	const checkOnlyPracticeContent = (
		<Box
			className={classes.sidebarContainer}
			sx={{
				display: 'flex',
				flexDirection: 'column',
				height: '100%',
				position: 'relative'
			}}
		>
			<Scrollbar options={{ suppressScrollX: true }}>
				<Box sx={{ ml: 2 }} />
				<Box
					sx={{
						alignItems: 'center',
						borderRadius: 1,
						display: 'flex',
						overflow: 'hidden',
						p: 2
					}}
				/>
				{isPaymentEnabled && (
					<Box sx={{ p: 2 }}>
						{checkOnlySection.map((section) => (
							<AccordionSection
								key={section.title}
								title={section.title}
								items={section.items}
								pathname={location.pathname}
								expanded={expandedSection === section.title}
								onChange={handleAccordionChange(section.title)}
								badgeCount={getTotalBadgeCount(section.items)}
							/>
						))}
					</Box>
				)}
			</Scrollbar>
			<BottomSection
				faqUrl={CHECK_ONLY_FAQ_URL}
				showResources={false}
				hideSettings={restrictStaffToPaymentsOnly}
			/>
		</Box>
	)
	return (
		<>
			<Box sx={{ display: { lg: 'none' } }}>
				<Drawer
					anchor="left"
					onClose={onMobileClose}
					open={openMobile}
					variant="temporary"
					slotProps={{
						paper: {
							sx: {
								backgroundColor: 'background.default',
								backgroundImage: 'none',
								boxShadow: 'none',
								width: DRAWER_WIDTH,
								paddingTop: HEADER_HEIGHT,
								paddingBottom: FOOTER_HEIGHT
							}
						}
					}}
				>
					{isCheckOnlyPractice(currentPracticeObject) ? checkOnlyPracticeContent : content}
				</Drawer>
			</Box>
			<>
				<Drawer
					anchor="left"
					open
					variant="persistent"
					slotProps={{
						paper: {
							sx: {
								display: { xs: 'none', lg: 'block' },
								borderRight: 0,
								width: DRAWER_WIDTH,
								position: 'relative'
							}
						}
					}}
				>
					{isCheckOnlyPractice(currentPracticeObject) ? checkOnlyPracticeContent : content}
				</Drawer>
			</>
		</>
	)
}

const getTotalBadgeCount = (items: NavItem[]) =>
	items.reduce((total, item) => {
		if (item.icon && React.isValidElement(item.icon) && item.icon.props.badgeContent) {
			return total + item.icon.props.badgeContent
		}
		return total
	}, 0)

const BottomSection = ({
	faqUrl,
	showResources,
	hideSettings = false
}: {
	faqUrl: string
	showResources: boolean
	hideSettings?: boolean
}) => (
	<Box
		sx={{
			p: 3,
			mt: 'auto'
		}}
	>
		<Grid container spacing={2} sx={{ mb: 2 }}>
			{/* Renders its own Grid row only when the practice is eligible. */}
			<CapitalGuideBanner />
			<Grid size={12}>
				<CapitalPromoTile />
			</Grid>
			<Grid size={12}>
				<ReferralProgramButton />
			</Grid>
		</Grid>
		<Grid container spacing={2}>
			<Grid size={hideSettings ? 12 : 6}>
				{showResources ? (
					<ResourcesMenu faqUrl={faqUrl} />
				) : (
					<Button
						fullWidth
						color="primary"
						sx={SIDEBAR_PILL_SX}
						size="small"
						data-cy="faq-button"
						target="_blank"
						rel="noreferrer"
						href={faqUrl}
					>
						FAQs
					</Button>
				)}
			</Grid>
			{!hideSettings && (
				<Grid size={6}>
					<Button
						fullWidth
						color="primary"
						sx={SIDEBAR_PILL_SX}
						size="small"
						data-cy="faq-button"
						component={Link}
						to="/dashboard/account"
					>
						Settings
					</Button>
				</Grid>
			)}
		</Grid>
	</Box>
)

export default DashboardSidebar
